/**
 * Shared Site Attribute Configuration
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { AttributeConfig, ActivityContext } from "../../types"

export const baseSiteConfig: Partial<AttributeConfig> = {
  attribute: gqlTypes.ActivityAttributeKind.Site,
  label: "Site",
  validator: () => ({ valid: true }), // Optional field
  order: 50,
}


const siteLabel = (activityKind: string): string => {
  if (activityKind === "filling") {
    return "Fill Site"
  }
  if (!activityKind) {
    return "Site"
  }
  return `${capitalize(activityKind)} Site`
}

/**
 * Site for Scheduled status
 * Pre-populated from order sites, editable
 */
export const siteForScheduled = (activityKind: string): AttributeConfig =>
({
  ...baseSiteConfig,
  label: siteLabel(activityKind),
  visibility: "visible",
  editable: true,
  required: false,
  helpText: `Where ${activityKind} will take place`,
  // Pre-populate from order's customer sites
  defaultValue: (ctx: ActivityContext) => {
    // Support both old and new schema (sites vs customer_sites)
    const siteList = ctx.order?.sites ?? []

    // For filling: use filling sites only (no default from order.sites which are customer sites)
    // Use runtime activity kind from context, not the static config parameter
    if (ctx.activity.kind === "filling") {
      return null
    }

    // For delivery or other: use first site in list (any type)
    if (siteList.length > 0) {
      const siteData = siteList[0].site || siteList[0]
      return siteData?.id || null
    }

    return null
  },
} as AttributeConfig)

/**
 * Site for Started/Completed status
 * Inherited from Scheduled, editable only if not set in previous status
 */
export const siteInherited = (
  activityKind: string,
  fromStatus: string,
  editable: boolean = false,
  required: boolean = false
): AttributeConfig =>
({
  ...baseSiteConfig,
  label: siteLabel(activityKind),
  visibility: "visible",
  editable: editable,
  required: required,
  order: 30, // Default order for site
  helpText: `Where this activity will take place`,

  // Inherit from previous status
  defaultValue: (ctx: ActivityContext) => {
    const prevSite = ctx.activity.attributes?.find(
      (attr) => attr.attribute === gqlTypes.ActivityAttributeKind.Site && attr.event_kind === (fromStatus as any)
    )
    return prevSite?.site?.id || null
  },
} as AttributeConfig)

/**
 * Hidden site
 */
export const siteHidden: AttributeConfig = {
  ...baseSiteConfig,
  visibility: "hidden",
  editable: false,
  required: false,
} as AttributeConfig

function capitalize(str: string): string {
  if (str === "filling") {
    return "Fill"
  }
  return str.charAt(0).toUpperCase() + str.slice(1)
}
